import { useState, type RefObject } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { refreshQueueNow } from '../../../shared/realtime/queueRefresh'
import { recordProcurementDownload } from '../../../shared/api/requests.api'
import { bulkUpdateItemStatus } from '../../../shared/api/requestItems.api'
import { generateProcurementRequest } from '../utils/generateProcurementRequest'
import {
  bulkCreateProcurementRequests,
  type BulkSupplierInput,
} from '../../../shared/api/procurementRequests.api'
import { SupplierSelectModal } from '../../procurement/components/SupplierSelectModal'
import { buildManufacturerReportText } from '../../procurement/queueActions'
import { toast } from '../../../shared/ui/toastStore'
import type { RequestItem } from '../../../shared/api/types'

type Props = {
  requestId: string
  requestNumber: string
  clientCode: string | null
  visibleItems: RequestItem[]
  uploadRef: RefObject<HTMLInputElement>
  isUploading: boolean
  uploadResult: string | null
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void
}

export function ProcurementActions({
  requestId,
  requestNumber,
  clientCode,
  visibleItems,
  uploadRef,
  isUploading,
  uploadResult,
  onUpload,
}: Props) {
  const queryClient = useQueryClient()
  const [supplierModalOpen, setSupplierModalOpen] = useState(false)

  // Открыть модалку выбора поставщиков. Дальше handleConfirmSuppliers создаёт
  // ProcurementRequest на каждого, скачивает соответствующие XLSX и обновляет
  // связанные queries.
  const handleDownload = () => {
    if (visibleItems.length === 0) return
    setSupplierModalOpen(true)
  }

  const handleConfirmSuppliers = async (
    suppliers: BulkSupplierInput[],
    note: string | null,
    manufacturer: string | null,
  ) => {
    if (visibleItems.length === 0) return
    const ids = visibleItems.map((i) => i.id)
    const itemById = new Map(visibleItems.map((i) => [i.id, i]))
    const { requests: created, manufacturerReport } = await bulkCreateProcurementRequests({
      requestItemIds: ids,
      suppliers,
      manufacturer: manufacturer ? { name: manufacturer } : undefined,
      note: note ?? undefined,
    })
    for (const req of created) {
      // BOM производителю — только вошедшие позиции (занятые коллегой исключены).
      const bomItems = req.items
        .map((bi) => (bi.requestItemId ? itemById.get(bi.requestItemId) : undefined))
        .filter((i): i is RequestItem => !!i)
      await generateProcurementRequest({
        clientCode,
        requestNumber: req.fileCode,
        isBatch: true,
        supplierName: req.supplierName,
        fileCode: req.fileCode,
        items: bomItems.map((item) => ({
          requestItemId: item.id,
          clientCode,
          requestNumber,
          partNumber: item.partNumber,
          brand: item.brand,
          quantity: item.quantity,
          comment: item.comment,
        })),
      })
    }
    if (manufacturerReport && manufacturerReport.excluded.length > 0) {
      const text = buildManufacturerReportText(manufacturerReport)
      if (text) toast.info(text)
    }
    // Старая логика: per-user state=REQUESTED + NEW→IN_QUEUE для совместимости
    // с procurementStatus заявки. Не блокируем при ошибке фиксации.
    // Трогаем только позиции, реально вошедшие хоть в один BOM (раскладка/
    // занятые производителем — остаются «Новая»).
    const sentIds = new Set(
      created.flatMap((r) => r.items.map((it) => it.requestItemId)).filter(Boolean),
    )
    const touched = visibleItems.filter((i) => sentIds.has(i.id))
    try {
      if (touched.length > 0) {
        await recordProcurementDownload(
          requestId,
          touched.map((i) => i.id),
        )
      }
      const toUpdate = touched.filter((i) => i.status === 'NEW')
      if (toUpdate.length > 0) {
        // Один bulk-запрос вместо залпа из N одиночных PATCH (инцидент 04.06.2026).
        await bulkUpdateItemStatus(
          toUpdate.map((i) => i.id),
          'IN_QUEUE',
        )
      }
    } catch {
      /* ignore */
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['requests'] }),
      queryClient.invalidateQueries({ queryKey: ['requests', requestId] }),
      refreshQueueNow(),
      queryClient.invalidateQueries({ queryKey: ['procurement-requests'] }),
      ...ids.map((itemId) =>
        queryClient.invalidateQueries({ queryKey: ['item-proc-state', itemId] }),
      ),
    ])
    setSupplierModalOpen(false)
  }

  // Скачать Excel «без поставщика»: файл «ноунейм», без ProcurementRequest
  // и без смены статусов позиций.
  const handleDownloadNoSupplier = async () => {
    if (visibleItems.length === 0) return
    await generateProcurementRequest({
      clientCode,
      requestNumber: 'ноунейм',
      isBatch: true,
      fileNameOverride: 'ноунейм',
      items: visibleItems.map((item) => ({
        requestItemId: item.id,
        clientCode,
        requestNumber,
        partNumber: item.partNumber,
        brand: item.brand,
        quantity: item.quantity,
        comment: item.comment,
      })),
    })
    setSupplierModalOpen(false)
  }

  return (
    <>
      <button
        onClick={handleDownload}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
          />
        </svg>
        Скачать запрос
      </button>
      <button
        onClick={() => uploadRef.current?.click()}
        disabled={isUploading}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
          />
        </svg>
        {isUploading ? 'Квоты загружаются…' : 'Загрузить ответ'}
      </button>
      {/* «Все квоты» — read-only сводка квот закупщиков (без «Цены менеджера»,
         только Китай/Производители) с бейджем min среди закупочных квот. */}
      <Link
        to={`/requests/${requestId}/quotes-overview`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
          />
        </svg>
        Все квоты
      </Link>
      <input
        ref={uploadRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={onUpload}
      />
      {/* Заметная плашка процесса — чтобы закупщик видел, что импорт идёт,
          и не запускал загрузку повторно (история 14.05 — дубли квот). */}
      {isUploading && (
        <span className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">
          <span className="w-3.5 h-3.5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
          Квоты загружаются, не закрывайте страницу…
        </span>
      )}
      {!isUploading && uploadResult && (
        <span
          className={`text-sm ${uploadResult.startsWith('Ошибка') ? 'text-red-500' : 'text-green-600'}`}
        >
          {uploadResult}
        </span>
      )}

      {supplierModalOpen && (
        <SupplierSelectModal
          itemsCount={visibleItems.length}
          onClose={() => setSupplierModalOpen(false)}
          onConfirm={handleConfirmSuppliers}
          onDownloadNoSupplier={handleDownloadNoSupplier}
        />
      )}
    </>
  )
}
